import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import "../login/login.css";
import "./WarCryRecording.css";
import { selectAdminToken } from "../../admin/sessionSlice";
import { saveWarCry } from "../../functions/saveWarCry";
import { s3PublicUrl } from "../../utils/s3PublicUrl";

const MAX_SECONDS = 30;
const BAR_COUNT = 28;

function readGameSettings() {
  try {
    return JSON.parse(sessionStorage.getItem("gameSettings") || "{}") || {};
  } catch {
    return {};
  }
}

function readUserData() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}") || {};
  } catch {
    return {};
  }
}

// MediaRecorder.isTypeSupported() only confirms the device can ENCODE a format — it says nothing
// about whether the same device's <video>/<audio> element can actually DECODE it back. These are
// checked through entirely separate codec pipelines and genuinely diverge on real devices
// (VP9 hardware encode without matching decode support is common on Android) — recording in a
// codec the device can produce but not play back plays as a silent, permanently black frame.
function canPlayback(kind, type) {
  try {
    const el = document.createElement(kind === "video" ? "video" : "audio");
    return !!el.canPlayType(type);
  } catch {
    return false;
  }
}

function pickMimeType(kind) {
  const candidates =
    kind === "video"
      ? ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm", "video/mp4"]
      : ["audio/webm", "audio/ogg", "audio/mp4"];
  for (const type of candidates) {
    if (window.MediaRecorder && MediaRecorder.isTypeSupported?.(type) && canPlayback(kind, type)) return type;
  }
  return "";
}

/** Step 2 of 3 — record (or upload) a video or audio war cry, max 30s, then confirm with Next.
 * Uploads straight to S3 via a presigned URL (see functions/saveWarCry.js) — a real recorded clip
 * easily exceeds API Gateway's request-size limit if sent through our own API directly. */
function WarCryRecording() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((s) => s.auth?.user);
  const adminToken = useSelector(selectAdminToken);
  const { data } = useSelector((state) => state.theme);

  const headingColor = data?.headingColor || "#D83307";
  const buttonBg = data?.buttonBgColor || "#D83307";
  const buttonText = data?.buttonTextColor || "#FFFFFF";
  const rawCover = data?.background_mob || data?.hunt_cover_image || data?.background_desk;
  const coverImage = rawCover ? s3PublicUrl(rawCover) : "";

  useEffect(() => {
    document.body.classList.add("login-static-bg");
    if (coverImage) {
      document.body.style.backgroundImage = `url("${encodeURI(coverImage)}")`;
    }
    return () => {
      document.body.classList.remove("login-static-bg");
      document.body.style.backgroundImage = "";
    };
  }, [coverImage]);

  const storedUserData = useMemo(readUserData, []);
  const gameSettings = useMemo(readGameSettings, []);

  const stageId = location.state?.stageId ?? gameSettings.stageId ?? null;
  const themeName = location.state?.themeName ?? gameSettings.themeName ?? "theme0";

  const tokenForApi = useMemo(() => {
    const t = adminToken != null ? String(adminToken).trim() : "";
    if (t.includes("&")) return t;
    const sid = user?.sessionId ?? storedUserData.sessionId ?? "";
    const oid = user?.organizationId ?? storedUserData.organizationId ?? "";
    return sid && oid ? `${sid}&${oid}` : "";
  }, [adminToken, user, storedUserData]);

  const uid = user?.userId ?? user?.userid ?? user?.id ?? storedUserData.userId ?? storedUserData.userid ?? storedUserData.id;

  const [mode, setMode] = useState("video"); // 'video' | 'audio'
  const [facingMode, setFacingMode] = useState("user");
  const [cameraReady, setCameraReady] = useState(false);
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [levels, setLevels] = useState(() => Array(BAR_COUNT).fill(6));
  const [preview, setPreview] = useState(null); // { blob, url, type: 'video'|'audio' }
  const [previewError, setPreviewError] = useState(false);

  const videoElRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const liveViewStreamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const startedAtRef = useRef(0);
  const timerRef = useRef(null);
  const autoStopRef = useRef(null);
  const stoppingRef = useRef(false);
  const galleryInputRef = useRef(null);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (liveViewStreamRef.current) {
      liveViewStreamRef.current.getTracks().forEach((t) => t.stop());
      liveViewStreamRef.current = null;
    }
    setCameraReady(false);
  };

  const cleanupAudioGraph = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    if (autoStopRef.current) clearTimeout(autoStopRef.current);
    autoStopRef.current = null;
  };

  // Live camera preview for the Video tab — runs whenever that tab is active and nothing has
  // been captured yet, so the player can frame themselves before hitting record.
  useEffect(() => {
    if (mode !== "video" || preview || recording) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode },
          audio: true,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        // The live self-view <video> gets its OWN cloned stream, never the exact same stream
        // object MediaRecorder will record from — a real, reproducible Chromium bug: a
        // MediaStream simultaneously bound to a playing <video> element AND consumed by
        // MediaRecorder comes out with a corrupted, frameless video track (plays back solid
        // black with degenerate 2x2 dimensions), even though both consumers work perfectly fine
        // on their own. Verified by recording the shared stream with and without a live preview
        // attached — only the shared case produces a broken recording.
        const liveViewStream = stream.clone();
        liveViewStreamRef.current = liveViewStream;
        if (videoElRef.current) videoElRef.current.srcObject = liveViewStream;
        setCameraReady(true);
      } catch {
        setCameraReady(false);
      }
    })();
    return () => {
      cancelled = true;
      // Deliberately NOT reacting to `recording` here (see guard above, read once per run) — if
      // this cleanup fired every time `recording` flips true, it would tear down the very stream
      // the MediaRecorder just started on, cutting the clip off almost instantly. Only stop the
      // stream when the thing that should actually end it (mode/facingMode/preview) changes.
      stopStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, facingMode, preview]);

  useEffect(() => () => {
    stopStream();
    cleanupAudioGraph();
  }, []);

  useEffect(() => {
    setPreviewError(false);
  }, [preview]);

  // Some devices can encode a codec in MediaRecorder that their own <video>/<audio> element then
  // can't decode back (asymmetric hardware codec support — canPlayType() can even claim "probably"
  // and still be wrong) — that plays back as a silent, permanently black/blank frame with no
  // indication anything is wrong. Surface it instead of failing silently, since the recording
  // itself is still fine to send even if this device can't preview it.
  const handlePreviewMediaError = (e) => {
    const err = e?.target?.error;
    console.warn("[WarCryRecording] preview playback failed:", err?.code, err?.message);
    setPreviewError(true);
  };

  const drawLevels = () => {
    const analyser = analyserRef.current;
    if (!analyser) return;
    const buffer = new Uint8Array(analyser.fftSize);
    analyser.getByteTimeDomainData(buffer);
    const step = Math.floor(buffer.length / BAR_COUNT) || 1;
    const next = [];
    for (let i = 0; i < BAR_COUNT; i++) {
      const sample = buffer[i * step] ?? 128;
      const amplitude = Math.abs(sample - 128) / 128;
      next.push(6 + Math.round(amplitude * 46));
    }
    setLevels(next);
    rafRef.current = requestAnimationFrame(drawLevels);
  };

  const handleRecordingStopped = () => {
    cleanupAudioGraph();
    setLevels(Array(BAR_COUNT).fill(6));

    // The actual encoded bytes match whatever mimeType the MediaRecorder was constructed with
    // (mediaRecorderRef.current.mimeType) — NOT chunksRef.current[0]?.type, which browsers
    // virtually always leave as an empty string on dataavailable events. Mislabeling the blob
    // (e.g. tagging real video/mp4 bytes as video/webm) makes the browser try to decode it as
    // the wrong codec, which plays back as a black/frozen frame with no visible video at all.
    const mimeType =
      mediaRecorderRef.current?.mimeType || chunksRef.current[0]?.type || (mode === "video" ? "video/webm" : "audio/webm");
    const blob = new Blob(chunksRef.current, { type: mimeType });
    chunksRef.current = [];
    if (blob.size === 0) return;

    setPreview({ blob, url: URL.createObjectURL(blob), type: mode });
    stopStream();
  };

  const startRecording = async () => {
    if (recording || uploading) return;
    try {
      let stream = streamRef.current;
      if (mode === "audio") {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;

        const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
        const audioCtx = new AudioContextCtor();
        audioCtxRef.current = audioCtx;
        const source = audioCtx.createMediaStreamSource(stream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        analyserRef.current = analyser;
        rafRef.current = requestAnimationFrame(drawLevels);
      }
      if (!stream) {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode }, audio: true });
        streamRef.current = stream;
        // See the identical comment in the idle-preview effect above — the live <video> must
        // never share the exact stream object MediaRecorder records from.
        const liveViewStream = stream.clone();
        liveViewStreamRef.current = liveViewStream;
        if (videoElRef.current) videoElRef.current.srcObject = liveViewStream;
      }

      const mimeType = pickMimeType(mode);
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = handleRecordingStopped;
      mediaRecorderRef.current = recorder;
      stoppingRef.current = false;
      recorder.start();

      startedAtRef.current = Date.now();
      setElapsedMs(0);
      setRecording(true);
      timerRef.current = setInterval(() => {
        setElapsedMs(Date.now() - startedAtRef.current);
      }, 100);
      autoStopRef.current = setTimeout(() => stopRecording(), MAX_SECONDS * 1000);
    } catch (err) {
      await Swal.fire("Camera/microphone needed", err?.message || "Couldn't access the camera or microphone.", "error");
    }
  };

  const stopRecording = () => {
    if (stoppingRef.current) return;
    stoppingRef.current = true;
    if (timerRef.current) clearInterval(timerRef.current);
    if (autoStopRef.current) clearTimeout(autoStopRef.current);
    setRecording(false);
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    }
  };

  const handleToggleRecord = () => {
    if (recording) stopRecording();
    else startRecording();
  };

  const switchMode = (next) => {
    if (recording || next === mode) return;
    stopStream();
    setPreview(null);
    setMode(next);
  };

  const flipCamera = () => {
    if (recording || mode !== "video") return;
    setFacingMode((f) => (f === "user" ? "environment" : "user"));
  };

  const onPickGallery = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    stopStream();
    setPreview({ blob: file, url: URL.createObjectURL(file), type: mode });
  };

  const retake = () => {
    setPreview(null);
  };

  const handleNext = async () => {
    if (!preview) return;
    if (!tokenForApi || (!stageId && !uid)) {
      await Swal.fire("Error", "Missing session — please rejoin the lobby.", "error");
      return;
    }
    setUploading(true);
    try {
      const guessExt = preview.blob.type?.split("/")?.[1]?.split(";")?.[0] || (preview.type === "video" ? "webm" : "webm");
      await saveWarCry({
        token: tokenForApi,
        stageId,
        userId: uid,
        mediaBlob: preview.blob,
        fileExt: guessExt,
        mediaType: preview.type,
      });

      sessionStorage.setItem("gameSettings", JSON.stringify({ ...gameSettings, stageId, themeName, war_cry_recorded: true }));
      navigate("/instructions", { replace: true, state: { stageId, themeName } });
    } catch (err) {
      await Swal.fire("Error", err?.message || "Failed to save your war cry", "error");
    } finally {
      setUploading(false);
    }
  };

  const seconds = Math.min(MAX_SECONDS, elapsedMs / 1000);
  const progressPct = (seconds / MAX_SECONDS) * 100;

  return (
    <div className="landing-page war-cry-page">
      <div className="landing-content war-cry-content">
        <div className="war-cry-step" style={{ color: buttonBg }}>STEP 2 OF 3</div>
        <h1 className="war-cry-title" style={{ color: headingColor }}>Say it like you mean it</h1>
        <p className="war-cry-sub">Record a video or audio message and send your warcry</p>

        <div className="war-cry-tabs">
          <button
            type="button"
            className={`war-cry-tab${mode === "video" ? " war-cry-tab--active" : ""}`}
            style={mode === "video" ? { background: buttonBg, color: buttonText } : undefined}
            onClick={() => switchMode("video")}
          >
            <i className="fa-solid fa-video" /> Video
          </button>
          <button
            type="button"
            className={`war-cry-tab${mode === "audio" ? " war-cry-tab--active" : ""}`}
            style={mode === "audio" ? { background: buttonBg, color: buttonText } : undefined}
            onClick={() => switchMode("audio")}
          >
            <i className="fa-solid fa-microphone" /> Audio
          </button>
        </div>

        <div className="war-cry-card">
          {mode === "video" ? (
            <div className="war-cry-video-frame">
              {preview ? (
                previewError ? (
                  <div className="war-cry-preview-fallback">
                    <i className="fa-solid fa-triangle-exclamation" />
                    <span>Preview isn't available on this device, but your recording is ready to send.</span>
                  </div>
                ) : (
                  // Distinct key from the live-preview element below forces React to mount a
                  // genuinely fresh <video> node here instead of reusing the same DOM element
                  // that was just playing a live srcObject stream — reusing it left residual
                  // internal decoder state behind that made the recorded blob play back as a
                  // permanently black, dimensionless frame despite the blob itself being fine.
                  <video
                    key="preview"
                    className="war-cry-video-el"
                    src={preview.url}
                    controls
                    playsInline
                    onError={handlePreviewMediaError}
                  />
                )
              ) : (
                <video key="live" ref={videoElRef} className="war-cry-video-el" autoPlay muted playsInline />
              )}
              {recording && !preview && (
                <span className="war-cry-rec-badge">
                  <span className="war-cry-rec-dot" /> REC {String(Math.floor(seconds)).padStart(2, "0")}:{String(Math.floor((seconds % 1) * 100)).padStart(2, "0")}
                </span>
              )}
              {!preview && (
                <button type="button" className="war-cry-flip-btn" onClick={flipCamera} disabled={recording} aria-label="Flip camera">
                  <i className="fa-solid fa-camera-rotate" />
                </button>
              )}
              {!cameraReady && !preview && !recording && (
                <div className="war-cry-video-placeholder">Waiting for camera…</div>
              )}
            </div>
          ) : preview ? (
            previewError ? (
              <div className="war-cry-preview-fallback">
                <i className="fa-solid fa-triangle-exclamation" />
                <span>Preview isn't available on this device, but your recording is ready to send.</span>
              </div>
            ) : (
              <audio className="war-cry-audio-el" src={preview.url} controls onError={handlePreviewMediaError} />
            )
          ) : (
            <div className="war-cry-waveform">
              {levels.map((h, i) => (
                <span
                  key={i}
                  className="war-cry-bar"
                  style={{ height: `${h}px`, background: i % 2 === 0 ? buttonBg : `${buttonBg}99` }}
                />
              ))}
            </div>
          )}

          <div className="war-cry-progress-track">
            <div className="war-cry-progress-fill" style={{ width: `${progressPct}%`, background: buttonBg }} />
          </div>
          <div className="war-cry-timer">
            0:{String(Math.floor(seconds)).padStart(2, "0")} / 0:{MAX_SECONDS}
          </div>
        </div>

        {!preview ? (
          <div className="war-cry-actions">
            <button type="button" className="war-cry-side-btn" onClick={() => galleryInputRef.current?.click()} disabled={recording}>
              <span className="war-cry-side-icon"><i className="fa-solid fa-image" /></span>
              Gallery
            </button>

            <button
              type="button"
              className={`war-cry-record-btn${recording ? " war-cry-record-btn--active" : ""}`}
              style={{ borderColor: buttonBg }}
              disabled={uploading}
              onClick={handleToggleRecord}
              aria-label={recording ? "Tap to stop" : "Tap to record"}
            >
              {recording ? (
                <span className="war-cry-record-stop" style={{ background: buttonBg }} />
              ) : (
                <span className="war-cry-record-dot" style={{ background: buttonBg }} />
              )}
            </button>

            <button type="button" className="war-cry-side-btn" onClick={flipCamera} disabled={recording || mode !== "video"}>
              <span className="war-cry-side-icon"><i className="fa-solid fa-rotate" /></span>
              Flip
            </button>
          </div>
        ) : (
          <div className="war-cry-actions">
            <button type="button" className="war-cry-side-btn" onClick={retake} disabled={uploading}>
              <span className="war-cry-side-icon"><i className="fa-solid fa-rotate-left" /></span>
              Retake
            </button>
          </div>
        )}

        <input
          ref={galleryInputRef}
          type="file"
          accept={mode === "video" ? "video/*" : "audio/*"}
          hidden
          onChange={onPickGallery}
        />

        <p className="war-cry-record-hint">
          {uploading ? "Saving your war cry..." : recording ? "Tap to stop" : preview ? "Ready to send" : "Tap to record"}
        </p>

        <div className="war-cry-tips">
          <div className="war-cry-tip">
            <i className="fa-regular fa-clock" />
            <span>Max {MAX_SECONDS} seconds</span>
          </div>
          <div className="war-cry-tip">
            <i className={`fa-solid ${mode === "video" ? "fa-eye" : "fa-volume-high"}`} />
            <span>{mode === "video" ? "Look at the camera and speak loud" : "Hold close to your mouth and speak loud"}</span>
          </div>
          <div className="war-cry-tip">
            <i className="fa-regular fa-face-smile" />
            <span>Be bold, be you!</span>
          </div>
        </div>

        <button
          type="button"
          className="landing-play-btn war-cry-next-btn"
          style={{ background: buttonBg, color: buttonText }}
          onClick={handleNext}
          disabled={!preview || uploading}
        >
          {uploading ? "Uploading..." : "Next"}
        </button>
      </div>
    </div>
  );
}

export default WarCryRecording;
